import {
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { TableCell } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, StaticDropdown } from "src/Components";
import {
  CoveredCallSendPriorityMode,
  ICoveredCallSendPriorityAccount,
  ICoveredCallSendPrioritySearchParams,
  ICoveredCallSendPriorityTableRow,
  ICoveredCallSendPriorityView,
} from "src/Interfaces/ccOrder";
import { Report } from "src/Interfaces/TableInterfaces";
import { DeltaHedgeOrderType } from "src/Interfaces/deltaHedgeOrder";
import {
  getCoveredCallSendPriorityAccounts,
  getCoveredCallSendPriorityConfig,
  getCoveredCallSendPriorityTable,
  refreshCoveredCallSendPriorities,
  setCoveredCallManualSendPriorities,
  setCoveredCallSendPriorityMode,
} from "src/Requests/ccOrder";
import TableTemplate, {
  IAdditional,
} from "src/Tables/TableTemplate/tableTemplate";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import { packetErrorHandler } from "src/Utils/requestUtils";
import { toast } from "react-toastify";

const tabs = [
  { id: DeltaHedgeOrderType.Open, title: "ورود" },
  { id: DeltaHedgeOrderType.Offset, title: "آفست" },
  { id: DeltaHedgeOrderType.Roll, title: "رول" },
];

const DEFAULT_OTHER_PRIORITY = 999;

const isAutomatic = (mode: CoveredCallSendPriorityMode) =>
  mode >= CoveredCallSendPriorityMode.FreeLiquidityAscending;

const isBaseInstrumentMode = (mode: CoveredCallSendPriorityMode) =>
  mode ===
    CoveredCallSendPriorityMode.BaseCoveredCallPortfolioPercentAscending ||
  mode ===
    CoveredCallSendPriorityMode.BaseCoveredCallPortfolioPercentDescending;

const getBaseInstrumentKey = (row: ICoveredCallSendPriorityTableRow) =>
  row.baseInstrumentId
    ? `id:${row.baseInstrumentId}`
    : `name:${row.baseInstrumentName}`;

const getBaseInstrumentTabs = (
  rows: ICoveredCallSendPriorityTableRow[],
) => {
  const instruments = new Map<string, string>();
  rows.forEach((row) => {
    const name = row.baseInstrumentName?.trim();
    if (!name) return;
    instruments.set(getBaseInstrumentKey(row), name);
  });

  return Array.from(instruments.entries())
    .map(([key, name]) => ({ key, name }))
    .sort((first, second) => first.name.localeCompare(second.name, "fa"));
};

const getServerMode = (view: ICoveredCallSendPriorityView) =>
  view.serverMode ?? view.mode;

const formatPersianDateTimeWithLatinDigits = (value: string) =>
  new Date(value).toLocaleString("fa-IR-u-ca-persian-nu-latn");

const getModeSelectionKey = (
  orderType: DeltaHedgeOrderType,
  mode: CoveredCallSendPriorityMode,
) => `${orderType}:${mode}`;

const sortRows = (rows: ICoveredCallSendPriorityTableRow[]) =>
  [...rows]
    .sort(
      (first, second) =>
        first.priority - second.priority ||
        first.accountName.localeCompare(second.accountName, "fa"),
    )
    .map((row, index) => ({ ...row, row: index + 1 }));

const SendPrioritySetting = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(DeltaHedgeOrderType.Open);
  const [views, setViews] = useState<
    Partial<Record<DeltaHedgeOrderType, ICoveredCallSendPriorityView>>
  >({});
  const [selectedModes, setSelectedModes] = useState<
    Partial<Record<DeltaHedgeOrderType, CoveredCallSendPriorityMode>>
  >({});
  const [onlyUsersWithOrders, setOnlyUsersWithOrders] = useState(true);
  const [manualRows, setManualRows] = useState<
    Partial<Record<DeltaHedgeOrderType, ICoveredCallSendPriorityTableRow[]>>
  >({});
  const [accounts, setAccounts] = useState<ICoveredCallSendPriorityAccount[]>(
    [],
  );
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [selectedBaseInstrumentKey, setSelectedBaseInstrumentKey] = useState<
    string | null
  >(null);
  const pendingEmptyModeRefresh = useRef<string | null>(null);

  const activeView = views[activeTab];
  const activeServerMode = activeView ? getServerMode(activeView) : null;
  const selectedMode = selectedModes[activeTab] ?? activeServerMode;
  const activeManualRows = useMemo(
    () => manualRows[activeTab] || [],
    [activeTab, manualRows],
  );
  const tableSearchParameters = useMemo(
    () => ({
      orderType: activeTab,
      mode: selectedMode ?? undefined,
      onlyUsersWithOrders,
    }),
    [activeTab, selectedMode, onlyUsersWithOrders],
  );

  const refreshTable = () => setTableRefreshKey((current) => current + 1);

  const loadTab = (orderType: DeltaHedgeOrderType) => {
    setLoading(true);
    getCoveredCallSendPriorityConfig(orderType)
      .then((view) => {
        setViews((current) => ({ ...current, [orderType]: view }));
        setSelectedModes((current) => ({
          ...current,
          [orderType]: getServerMode(view),
        }));
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isOpen) return;
    pendingEmptyModeRefresh.current = null;
    setSelectedBaseInstrumentKey(null);
    setViews({});
    setSelectedModes({});
    setManualRows({});
    loadTab(activeTab);
    getCoveredCallSendPriorityAccounts()
      .then(setAccounts)
      .catch(packetErrorHandler);
  }, [isOpen]);

  const changeTab = (orderType: DeltaHedgeOrderType) => {
    pendingEmptyModeRefresh.current = null;
    setSelectedBaseInstrumentKey(null);
    setActiveTab(orderType);
    setSelectedAccountId(null);
    loadTab(orderType);
  };

  const selectMode = (mode: CoveredCallSendPriorityMode | null) => {
    if (!activeView?.canManage || mode === null) return;
    if (mode === selectedMode) return;
    setSelectedBaseInstrumentKey(null);
    pendingEmptyModeRefresh.current = isAutomatic(mode)
      ? getModeSelectionKey(activeTab, mode)
      : null;
    setSelectedModes((current) => ({ ...current, [activeTab]: mode }));
  };

  const saveMode = () => {
    if (
      !activeView?.canManage ||
      selectedMode === null ||
      selectedMode === activeServerMode
    )
      return;
    setLoading(true);
    setCoveredCallSendPriorityMode(activeTab, selectedMode)
      .then((response) => {
        toast.success(response.msg);
        return getCoveredCallSendPriorityConfig(activeTab);
      })
      .then((view) => {
        setViews((current) => ({ ...current, [activeTab]: view }));
        setSelectedModes((current) => ({
          ...current,
          [activeTab]: getServerMode(view),
        }));
        refreshTable();
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const updatePriority = (rowId: number, priority: number) => {
    if (!Number.isInteger(priority) || priority < 1) return;
    setManualRows((current) => ({
      ...current,
      [activeTab]: (current[activeTab] || []).map((row) =>
        row.id === rowId ? { ...row, priority } : row,
      ),
    }));
  };

  const removeAccount = (accountId?: number | null) => {
    if (!accountId) return;
    setManualRows((current) => ({
      ...current,
      [activeTab]: (current[activeTab] || []).filter(
        (row) => row.accountId !== accountId,
      ),
    }));
  };

  const availableAccounts = useMemo(() => {
    const configured = new Set(
      activeManualRows
        .map((row) => row.accountId)
        .filter((id): id is number => !!id),
    );
    return accounts.filter((account) => !configured.has(account.id));
  }, [accounts, activeManualRows]);

  const addAccount = () => {
    if (!selectedAccountId) return;
    const account = accounts.find((item) => item.id === selectedAccountId);
    if (!account) return;
    const maxPriority = Math.max(
      0,
      ...activeManualRows
        .filter((row) => !row.isOther)
        .map((row) => row.priority),
    );
    const newRow: ICoveredCallSendPriorityTableRow = {
      id: account.id,
      row: activeManualRows.length + 1,
      accountId: account.id,
      accountName: account.name,
      baseInstrumentId: null,
      baseInstrumentName: "",
      priority: maxPriority + 1,
      isOther: false,
      metricValue: null,
      metricDisplay: "-",
    };
    setManualRows((current) => ({
      ...current,
      [activeTab]: [...(current[activeTab] || []), newRow],
    }));
    setSelectedAccountId(null);
  };

  const saveManual = () => {
    const otherRow = activeManualRows.find((row) => row.isOther);
    const items = activeManualRows
      .filter((row) => !row.isOther && row.accountId)
      .map((row) => ({
        accountId: row.accountId as number,
        priority: row.priority,
      }));
    setLoading(true);
    setCoveredCallManualSendPriorities(
      activeTab,
      items,
      otherRow?.priority ||
        activeView?.otherPriority ||
        DEFAULT_OTHER_PRIORITY,
    )
      .then((response) => {
        toast.success(response.msg);
        return getCoveredCallSendPriorityConfig(activeTab);
      })
      .then((view) => {
        setViews((current) => ({ ...current, [activeTab]: view }));
        refreshTable();
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const refreshAutomatic = () => {
    if (
      !activeView ||
      selectedMode === null ||
      !isAutomatic(selectedMode)
    )
      return;
    pendingEmptyModeRefresh.current = null;
    setLoading(true);
    refreshCoveredCallSendPriorities(activeTab, selectedMode)
      .then((response) => {
        toast.success(response.msg);
        return getCoveredCallSendPriorityConfig(activeTab);
      })
      .then((view) => {
        setViews((current) => ({ ...current, [activeTab]: view }));
        refreshTable();
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const manualAdditional = useMemo<
    IAdditional<ICoveredCallSendPriorityTableRow>
  >(
    () => ({
      getHeader: () => (
        <>
          <TableCell className="!bg-white !text-center !font-black">
            اولویت تخصیص
          </TableCell>
          <TableCell className="!w-16 !bg-white" />
        </>
      ),
      getRow: ({ row }) => (
        <>
          <TableCell onClick={(event) => event.stopPropagation()}>
            <input
              type="number"
              min={1}
              value={row.priority}
              onChange={(event) =>
                updatePriority(row.id, Number(event.target.value))
              }
              className="mx-auto block h-8 w-24 rounded border border-gray-300 px-2 text-center"
            />
          </TableCell>
          <TableCell
            className="!w-16 !text-center"
            onClick={(event) => event.stopPropagation()}
          >
            {!row.isOther ? (
              <button
                type="button"
                title="حذف"
                className="text-red-600"
                onClick={() => removeAccount(row.accountId)}
              >
                <DeleteOutlined />
              </button>
            ) : null}
          </TableCell>
        </>
      ),
    }),
    [activeTab],
  );

  const prepareReports = (
    reports: Report<ICoveredCallSendPriorityTableRow>,
    baseInstrumentKey: string | null = null,
  ): Report<ICoveredCallSendPriorityTableRow> => {
    if (!activeView) return reports;

    const editableManual =
      selectedMode === CoveredCallSendPriorityMode.Manual &&
      activeView.canManage;
    const rows = editableManual ? activeManualRows : reports.report;
    const visibleRows =
      selectedMode !== undefined &&
      selectedMode !== null &&
      isBaseInstrumentMode(selectedMode) &&
      baseInstrumentKey
        ? rows
            .filter((row) => getBaseInstrumentKey(row) === baseInstrumentKey)
            .map((row, index) => ({ ...row, row: index + 1 }))
        : rows;
    return {
      ...reports,
      count: visibleRows.length,
      report: editableManual ? sortRows(visibleRows) : visibleRows,
      translatedHeaders: reports.translatedHeaders.filter((header) => {
        if (
          (selectedMode === null || !isAutomatic(selectedMode)) &&
          header.headerName === "MetricValue"
        )
          return false;
        if (
          (selectedMode === null || !isBaseInstrumentMode(selectedMode)) &&
          header.headerName === "BaseInstrumentName"
        )
          return false;
        if (editableManual && header.headerName === "Priority") return false;
        return true;
      }),
    };
  };

  const modalUI = (
    <div dir="rtl" className="flex min-h-[520px] w-full flex-col gap-4">
      <div className="text-base font-bold">اولویت تخصیص</div>
      <div className="flex border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`min-w-28 border-b-2 px-6 py-2 text-sm ${
              activeTab === tab.id
                ? "border-sky-600 text-sky-700"
                : "border-transparent text-gray-500"
            }`}
            onClick={() => changeTab(tab.id)}
          >
            {tab.title}
          </button>
        ))}
      </div>

      {activeView ? (
        <>
          <div className="flex flex-col gap-1 pb-1">
            <div className="flex flex-nowrap items-end gap-3 overflow-x-auto">
              <div className="w-[320px] shrink-0">
                <StaticDropdown
                  dropdown={{
                    name: "mode",
                    placeholder: "روش تخصیص",
                    extraLabel: true,
                    disabled: !activeView.canManage || loading,
                  }}
                  options={activeView.availableModes}
                  value={selectedMode}
                  onChange={(_, value) =>
                    selectMode(
                      value === null
                        ? null
                        : (Number(value) as CoveredCallSendPriorityMode),
                    )
                  }
                />
              </div>
              {activeView.canManage ? (
                <button
                  type="button"
                  disabled={
                    loading ||
                    selectedMode === null ||
                    selectedMode === activeServerMode
                  }
                  onClick={saveMode}
                  className="h-9 shrink-0 rounded bg-sky-600 px-5 text-xs text-white disabled:opacity-50"
                >
                  تغییر
                </button>
              ) : null}
              <label className="flex h-9 shrink-0 cursor-pointer items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={onlyUsersWithOrders}
                  onChange={(event) => {
                    setSelectedBaseInstrumentKey(null);
                    setOnlyUsersWithOrders(event.target.checked);
                  }}
                />
                نمایش کاربران با سفارش
              </label>

              {selectedMode !== null && isAutomatic(selectedMode) ? (
                <>
                  {selectedMode === activeServerMode ? (
                    <span className="shrink-0 pb-2 text-xs text-gray-500">
                      آخرین محاسبه:{" "}
                      {activeView.updatedAt ? (
                        <span dir="ltr">
                          {formatPersianDateTimeWithLatinDigits(
                            activeView.updatedAt,
                          )}
                        </span>
                      ) : (
                        "انجام نشده"
                      )}
                    </span>
                  ) : null}
                  {activeView.canManage ? (
                    <button
                      type="button"
                      aria-label="Refresh"
                      title="Refresh"
                      disabled={loading}
                      onClick={refreshAutomatic}
                      className="mr-auto flex h-9 w-9 shrink-0 items-center justify-center rounded bg-sky-600 text-xs text-white disabled:opacity-50"
                    >
                      <ReloadOutlined spin={loading} />
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
            <span className="w-[320px] shrink-0 text-xs text-gray-500">
              روش تخصیص انتخابی: {activeView.availableModes.find(
                (mode) => mode.id === activeServerMode,
              )?.name ?? "-"}
            </span>
          </div>

          {selectedMode === CoveredCallSendPriorityMode.Manual &&
          activeView.canManage ? (
            <div className="flex items-end gap-2 rounded bg-gray-50 p-3">
              <div className="min-w-[280px]">
                <StaticDropdown
                  dropdown={{
                    name: "accountId",
                    placeholder: "انتخاب اکانت",
                  }}
                  options={availableAccounts}
                  value={selectedAccountId}
                  onChange={(_, value) =>
                    setSelectedAccountId(value === null ? null : Number(value))
                  }
                />
              </div>
              <button
                type="button"
                disabled={!selectedAccountId}
                onClick={addAccount}
                className="flex h-9 items-center gap-2 rounded bg-sky-600 px-4 text-xs text-white disabled:opacity-50"
              >
                <PlusOutlined /> افزودن
              </button>
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-hidden">
            <TableTemplate<
              ICoveredCallSendPrioritySearchParams,
              ICoveredCallSendPriorityTableRow
            >
              key={`${activeTab}-${selectedMode ?? "none"}-${onlyUsersWithOrders}`}
              getDataTable={getCoveredCallSendPriorityTable}
              searchParameters={tableSearchParameters}
              pagesize={5000}
              silentRefreshKey={tableRefreshKey}
              onResponse={(response) => {
                if (
                  selectedMode === CoveredCallSendPriorityMode.Manual
                ) {
                  setManualRows((current) => ({
                    ...current,
                    [activeTab]: response.report,
                  }));
                }

                if (selectedMode === null) return;
                const selectionKey = getModeSelectionKey(
                  activeTab,
                  selectedMode,
                );
                if (pendingEmptyModeRefresh.current !== selectionKey) return;

                pendingEmptyModeRefresh.current = null;
                if (!response.report.length) refreshAutomatic();
              }}
              TableUI={({ reports, onAction, sortingType, setSortingType }) => {
                const baseInstrumentTabs = getBaseInstrumentTabs(
                  reports.report,
                );
                const hasSelectedBaseInstrument = baseInstrumentTabs.some(
                  (item) => item.key === selectedBaseInstrumentKey,
                );
                const activeBaseInstrumentKey = hasSelectedBaseInstrument
                  ? selectedBaseInstrumentKey
                  : null;

                return (
                  <div className="flex min-h-0 flex-col gap-2">
                    {selectedMode !== null &&
                    isBaseInstrumentMode(selectedMode) ? (
                      <div
                        role="group"
                        aria-label="فیلتر نماد پایه"
                        className="flex shrink-0 flex-nowrap items-center gap-1 overflow-x-auto border-b border-gray-200 pb-2"
                      >
                        <button
                          type="button"
                          aria-pressed={activeBaseInstrumentKey === null}
                          onClick={() => setSelectedBaseInstrumentKey(null)}
                          className={`shrink-0 rounded px-4 py-1.5 text-xs transition-colors ${
                            activeBaseInstrumentKey === null
                              ? "bg-sky-600 text-white"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          همه
                        </button>
                        {baseInstrumentTabs.map((item) => (
                          <button
                            key={item.key}
                            type="button"
                            aria-pressed={activeBaseInstrumentKey === item.key}
                            onClick={() =>
                              setSelectedBaseInstrumentKey(item.key)
                            }
                            className={`shrink-0 rounded px-4 py-1.5 text-xs transition-colors ${
                              activeBaseInstrumentKey === item.key
                                ? "bg-sky-600 text-white"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            {item.name}
                          </button>
                        ))}
                      </div>
                    ) : null}
                    <CommonTableUI
                      reports={prepareReports(
                        reports,
                        activeBaseInstrumentKey,
                      )}
                      onAction={onAction}
                      sortingType={sortingType}
                      setSortingType={setSortingType}
                      AdditionalBody={
                        selectedMode === CoveredCallSendPriorityMode.Manual &&
                        activeView.canManage
                          ? manualAdditional
                          : undefined
                      }
                      tableBoxClassName="!mx-auto !max-h-[360px] !max-w-[800px]"
                    />
                  </div>
                );
              }}
            />
          </div>

          {selectedMode === CoveredCallSendPriorityMode.Manual &&
          activeView.canManage ? (
            <div className="flex justify-end">
              <button
                type="button"
                disabled={loading}
                onClick={saveManual}
                className="h-9 rounded bg-sky-600 px-7 text-xs text-white disabled:opacity-50"
              >
                ذخیره تغییرات
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <div className="flex flex-1 items-center justify-center text-sm text-gray-400">
          {loading ? "در حال دریافت اطلاعات..." : "اطلاعاتی وجود ندارد."}
        </div>
      )}
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex min-w-0 w-full items-center gap-2 rounded border-2 border-gray-300 px-2 py-3 text-xs transition-all hover:border-gray-500"
      >
        <SettingOutlined />
        <span>اولویت تخصیص</span>
      </button>
      {isOpen ? (
        <Modal
          UI={modalUI}
          onClose={() => setIsOpen(false)}
          modalBoxClassName="!w-[850px] !max-w-[95vw]"
        />
      ) : null}
    </>
  );
};

export default SendPrioritySetting;
